// Resolves an image field's reference image to something an <img src> can use.
// Nothing about the URL is persisted — it is derived here, at render time:
//
//   - a pending pick (`fieldImageFile` is still a File) → an object URL held in
//     local state and revoked when the pick changes or the component unmounts;
//   - otherwise the stored media id → GET media/response/get-form-media/:id.
//
// RTK Query caches per id, so several consumers of the same image (the canvas
// card and the properties panel) share one request.
//
// Call this at component top level, never inside a branch — both call sites
// render for every element type, and the `skip` covers the non-image ones.

import { useEffect, useState } from 'react';
import { useGetFormMediaQuery } from '@/api/endpoints/mediaApi';
import type { FieldImageRef } from './formElementDefaults';

export const useFieldImageUrl = (
  img: FieldImageRef | null | undefined,
  file?: unknown,
): string | undefined => {
  const pending = file instanceof File ? file : null;

  const [objectUrl, setObjectUrl] = useState<string>();
  useEffect(() => {
    if (!pending) {
      setObjectUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(pending);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pending]);

  // A pending pick wins, so don't fetch the (possibly stale) stored image.
  const { data } = useGetFormMediaQuery(img?.id ?? '', {
    skip: !img?.id || Boolean(pending),
  });

  return objectUrl ?? data?.url;
};

export default useFieldImageUrl;
